/**
 * FormField — V2.I.9
 *
 * Wrapper for form inputs that provides :
 *  - a uniform <label> + optional hint
 *  - a uniform red error message (sr-announced via aria-live)
 *  - aria-invalid + aria-describedby plumbing on the underlying control
 *  - red border when error is set
 *
 * Why : every form across the app reimplements its own label / error
 * styling, leading to inconsistencies (some forms surface errors as
 * toasts, some inline, some not at all). This wrapper standardises the
 * inline path that 90% of forms want.
 *
 * Pairs nicely with `useFormState` (`form.errorIfTouched('name')`).
 *
 * Usage :
 *   <FormField label="Email" hint="On ne le partagera pas" error={form.errorIfTouched('email')} required>
 *     <input type="email" value={form.values.email} ... />
 *   </FormField>
 *
 * The wrapper auto-sets `aria-invalid` + `aria-describedby` on the
 * single child input element by cloning it. If you need control over
 * the focus ring colour, override via the `--field-error-border` CSS
 * variable.
 */

import { Children, cloneElement, isValidElement, useId, type ReactNode } from 'react';

interface FormFieldProps {
  /** Visible label rendered above the control. */
  label: string;
  /** Optional helper text (rendered in muted colour below the label). */
  hint?: string;
  /** Error message — when set, the field shows the red error state. */
  error?: string | undefined;
  /** Asterisk on the label (visual + aria-required on the input). */
  required?: boolean;
  /** The actual input / select / textarea. ONE element expected. */
  children: ReactNode;
  /** Override the wrapper class when needed. */
  className?: string;
}

export function FormField({
  label,
  hint,
  error,
  required,
  children,
  className,
}: FormFieldProps) {
  const fieldId = useId();
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;

  const child = Children.only(children);
  const cloned = isValidElement(child)
    ? cloneElement(child as React.ReactElement<Record<string, unknown>>, {
        id: (child.props as Record<string, unknown>).id ?? fieldId,
        'aria-invalid': error ? 'true' : undefined,
        'aria-describedby':
          [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined,
        'aria-required': required ? 'true' : undefined,
        style: {
          ...((child.props as { style?: React.CSSProperties }).style || {}),
          ...(error
            ? {
                borderColor: 'var(--field-error-border, var(--danger))',
                boxShadow: '0 0 0 1px var(--field-error-border, var(--danger))',
              }
            : {}),
        },
      })
    : child;

  return (
    <div className={className} style={{ marginBottom: '12px' }}>
      <label
        htmlFor={fieldId}
        style={{
          display: 'block',
          marginBottom: '6px',
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--text)',
        }}
      >
        {label}
        {required && (
          <span style={{ color: 'var(--danger)', marginLeft: '4px' }} aria-hidden>
            *
          </span>
        )}
      </label>
      {hint && !error && (
        <p
          id={hintId}
          style={{
            margin: '0 0 6px',
            fontSize: '12px',
            color: 'var(--muted)',
            lineHeight: 1.4,
          }}
        >
          {hint}
        </p>
      )}
      {cloned}
      {error && (
        <p
          id={errorId}
          role="alert"
          aria-live="polite"
          style={{
            margin: '6px 0 0',
            fontSize: '12px',
            color: 'var(--danger)',
            fontWeight: 500,
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
}
